<script lang="ts">
	import { filter, type Content, type Ordering } from '@prismicio/client';
	import type { SliceComponentProps } from '@prismicio/svelte';
	import type { GameDocument } from '../../../prismicio-types';
	import { inview } from 'svelte-inview';
	import { fade } from 'svelte/transition';
	import { combinePageArrays, getPaginatedDocument } from '$lib/helpers/app.documents';
	import Fixture from '$lib/components/Fixture.svelte';
	import Result from '$lib/components/Result.svelte';

	type Props = SliceComponentProps<Content.GameListSlice>;

	const { slice }: Props = $props();
	const now = new Date();

	let games: GameDocument[] = $state([]);
	let loading: boolean = $state(false);
	let nextPage: boolean = $state(true);
	let pageNumber: number = $state(1);
	let perPage: number = slice.primary.per_page ?? 10;
	let filters: string[] = [
		slice.primary.type == true
			? filter.dateAfter('my.game.date_time', now)
			: filter.dateBefore('my.game.date_time', now)
	];
	let ordering: Ordering[] = [
		{
			field: 'my.game.date_time',
			direction: slice.primary.type == true ? 'asc' : 'desc'
		}
	];

	const loadGames = async (useCombine: boolean) => {
		if (loading) return;

		loading = true;
		await getPaginatedDocument('game', perPage, pageNumber, nextPage, filters, ordering)
			.then((response) => {
				console.log(response);
				if (!response) {
					throw new Error('Error retrieving documents');
				}

				const results = response.results as GameDocument[];
				if (games && useCombine) {
					games = combinePageArrays(games, results);
				} else {
					games = results;
				}

				pageNumber = response.pageNum + 1;
				nextPage = response.nextPage;
			})
			.catch((error) => console.error(error))
			.finally(() => (loading = false));
	};
</script>

<section
	class="game-list__{slice.variation} spacing-top-{slice.primary.spacing_top} spacing-bottom-{slice
		.primary.spacing_bottom}"
	class:visually-hidden={games.length < 1 && slice.variation == 'preview'}
	data-slice-type={slice.slice_type}
	data-slice-variation={slice.variation}
>
	<div class="container">
		{#if slice.variation == 'preview'}
			<div
				class="game-list__{slice.variation}--list"
				use:inview
				oninview_init={() => loadGames(false)}
			>
				<h2 class="game-list__{slice.variation}--title text-lg-center mb-2 mb-lg-5">
					{slice.primary.type == true ? 'Next Game' : 'Latest Results'}
				</h2>

				{#if games.length}
					<div class="row justify-content-center gy-2 gy-lg-3">
						{#each games as game, i (i)}
							<div class="col-12 col-lg-10 col-hd-9" transition:fade>
								{#if slice.primary.type == true}
									<Fixture {game} highlight={i < 1} showtag={false} />
								{:else}
									<Result {game} highlight={i < 1} showtag={false} />
								{/if}
							</div>
						{/each}
					</div>
				{/if}

				<div class="d-flex justify-content-center mt-3 mb-lg-5">
					{#if slice.primary.type == true}
						<a href="/fixtures" class="btn btn-sm btn-primary">View all fixtures</a>
					{:else}
						<a href="/results" class="btn btn-sm btn-primary">View all results</a>
					{/if}
				</div>
			</div>
		{:else}
			{#if games.length}
				<div class="row justify-content-center gy-2 gy-lg-3">
					{#each games as game, i (i)}
						<div class="col-12 col-lg-10 col-hd-9" transition:fade>
							{#if slice.primary.type == true}
								<Fixture {game} highlight={i < 1} showtag={true} />
							{:else}
								<Result {game} highlight={i < 1} showtag={true} />
							{/if}
						</div>
					{/each}
				</div>
			{:else}
				<div class="row justify-content-center gy-2">
					<div class="col-12 col-lg-10 col-hd-8">
						<h3 class="text-center">
							{slice.primary.type == true ? 'No upcoming fixtures' : 'No results'}
						</h3>
					</div>
				</div>
			{/if}

			{#if nextPage}
				<div class="d-flex justify-content-center mt-5">
					<button
						class="btn btn-lg btn-text"
						onclick={() => loadGames(true)}
						use:inview
						oninview_enter={() => loadGames(true)}
					>
						{#if loading}
							<span>Loading...</span>
						{:else}
							<span transition:fade>Load more</span>
						{/if}
					</button>
				</div>
			{/if}
		{/if}
	</div>
</section>
